import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import SignalWifiOffIcon from "@mui/icons-material/SignalWifiOff";
import WifiIcon from "@mui/icons-material/Wifi";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const InternetConnectionModal = ({
  open,
  onClose,
  isReconnected = false,
  onConnectionRestored,
}) => {
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showRestartAppDialog, setShowRestartAppDialog] = useState(false);

  useEffect(() => {
    const shouldCheck = open && !isReconnected;

    if (shouldCheck) {
      checkInternetConnection();
    }
  }, [open, isReconnected]);

  const checkInternetConnection = async () => {
    setIsCheckingConnection(true);
    setConnectionStatus(null);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch("https://www.google.com/favicon.ico", {
        method: "HEAD",
        mode: "no-cors",
        cache: "no-store",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      setConnectionStatus("success");
      setIsCheckingConnection(false);

      if (onConnectionRestored) {
        onConnectionRestored();
      }
    } catch (error) {
      setConnectionStatus("error");
      setIsCheckingConnection(false);
    }
  };

  const handleOpenRestartDialog = () => {
    setShowRestartAppDialog(true);
  };

  const handleCloseRestartDialog = () => {
    setShowRestartAppDialog(false);
  };

  const handleConfirmClose = () => {
    setShowRestartAppDialog(false);
    onClose();
  };

  const getTitle = () => {
    if (isReconnected) {
      return "Conexão Restabelecida!";
    }

    return "Sem Conexão com a Internet";
  };

  const getDescription = () => {
    if (isReconnected) {
      return "Sua conexão com a internet foi restabelecida com sucesso.";
    }

    return "Não foi possível estabelecer conexão com a internet. Verifique sua rede.";
  };

  const title = getTitle();
  const description = getDescription();
  const showConnectionTest = !isReconnected && connectionStatus !== null;

  return (
    <>
      <Modal
        open={open}
        onClose={isReconnected ? onClose : undefined}
        aria-labelledby="internet-connection-title"
        disableEscapeKeyDown={!isReconnected}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <IconButton
              sx={{
                color: isReconnected ? "success.main" : "error.main",
                fontSize: 64,
                mb: 2,
              }}
              disableRipple
            >
              {isReconnected ? (
                <WifiIcon fontSize="inherit" />
              ) : (
                <SignalWifiOffIcon fontSize="inherit" />
              )}
            </IconButton>

            <Typography
              id="internet-connection-title"
              variant="h5"
              component="h2"
              sx={{
                fontWeight: "bold",
                mb: 1,
                textAlign: "center",
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              {description}
            </Typography>
          </Box>

          {isReconnected && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>✓ Conexão Ativa</AlertTitle>A internet está
              funcionando normalmente. Para garantir que as impressões funcionem
              corretamente, recomendamos reiniciar o aplicativo Slapie.
            </Alert>
          )}

          {!isReconnected && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Sem Internet</AlertTitle>
              Não foi possível detectar uma conexão ativa com a internet. Isso
              pode afetar o funcionamento do sistema.
            </Alert>
          )}

          {showConnectionTest && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity={
                  connectionStatus === "success"
                    ? "success"
                    : connectionStatus === "error"
                    ? "error"
                    : "info"
                }
                icon={
                  isCheckingConnection ? (
                    <CircularProgress size={20} />
                  ) : connectionStatus === "success" ? (
                    <CheckCircleIcon />
                  ) : connectionStatus === "error" ? (
                    <ErrorIcon />
                  ) : null
                }
              >
                <AlertTitle>
                  {isCheckingConnection
                    ? "Verificando conexão..."
                    : connectionStatus === "success"
                    ? "✓ Internet Funcionando!"
                    : connectionStatus === "error"
                    ? "✗ Ainda Sem Conexão"
                    : "Verificar conexão"}
                </AlertTitle>
                {connectionStatus === "success" && (
                  <Typography variant="body2">
                    A internet está funcionando. Você pode fechar esta janela e
                    continuar trabalhando.
                  </Typography>
                )}
                {connectionStatus === "error" && (
                  <Typography variant="body2">
                    A conexão com a internet ainda não foi estabelecida.
                    Verifique seus cabos de rede ou Wi-Fi.
                  </Typography>
                )}
              </Alert>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
              {isReconnected ? "Próximos Passos" : "Como resolver o problema?"}
            </Typography>

            {isReconnected ? (
              <Box component="ol" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Reinicie o aplicativo Slapie</strong> para garantir
                  que a conexão com a impressora funcione corretamente
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Procure o ícone do <strong>Slapie</strong> na barra de tarefas
                  do Windows (canto inferior direito)
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Clique com o botão direito no ícone e selecione{" "}
                  <strong>"Sair"</strong>
                </Typography>
                <Typography component="li" variant="body2">
                  Abra o aplicativo novamente pelo menu Iniciar ou área de
                  trabalho
                </Typography>
              </Box>
            ) : (
              <Box component="ol" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Verifique o cabo de rede</strong> - certifique-se de
                  que está conectado corretamente
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Verifique o Wi-Fi</strong> - confirme se está
                  conectado à rede correta
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Reinicie o roteador</strong> - desligue por 10
                  segundos e ligue novamente
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Aguarde alguns instantes e clique em{" "}
                  <strong>"Verificar Conexão"</strong> abaixo
                </Typography>
                <Typography component="li" variant="body2">
                  Se o problema persistir, entre em contato com seu provedor de
                  internet
                </Typography>
              </Box>
            )}
          </Box>

          {!isReconnected && connectionStatus === "error" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>⚠ Impacto no Sistema</AlertTitle>
              Sem conexão com a internet, algumas funcionalidades podem não
              funcionar corretamente, incluindo sincronização de pedidos e
              impressões.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: "column",
            }}
          >
            {isReconnected ? (
              <>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleOpenRestartDialog}
                  startIcon={<RestartAltIcon />}
                  fullWidth
                >
                  Como Reiniciar o App Slapie
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={onClose}
                  fullWidth
                >
                  Fechar
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={checkInternetConnection}
                  startIcon={
                    isCheckingConnection ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  disabled={isCheckingConnection}
                  fullWidth
                >
                  {isCheckingConnection
                    ? "Verificando..."
                    : "Verificar Conexão"}
                </Button>
              </>
            )}
          </Box>

          {!isReconnected && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "text.secondary",
                mt: 2,
              }}
            >
              Esta janela permanecerá aberta até que a conexão seja
              restabelecida
            </Typography>
          )}

          {isReconnected && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "text.secondary",
                mt: 2,
              }}
            >
              Reinicie o app Slapie para garantir o melhor funcionamento do
              sistema
            </Typography>
          )}
        </Box>
      </Modal>

      <Dialog
        open={showRestartAppDialog}
        onClose={handleCloseRestartDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RestartAltIcon color="warning" />
            Como Reiniciar o App Slapie
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Siga os passos abaixo para reiniciar o aplicativo Slapie:
          </DialogContentText>
          <Box component="ol" sx={{ color: "text.secondary" }}>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Localize o <strong>ícone do Slapie</strong> na barra de tarefas
                do Windows (canto inferior direito, próximo ao relógio)
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Clique com o botão direito</strong> no ícone do Slapie
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                No menu que aparecer, selecione a opção <strong>"Sair"</strong>{" "}
                ou <strong>"Fechar"</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Aguarde alguns segundos até o aplicativo fechar completamente
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Abra o aplicativo Slapie novamente pelo menu Iniciar do Windows
                ou pelo atalho na área de trabalho
              </Typography>
            </li>
            <li>
              <Typography variant="body2" fontWeight={"bold"}>
                Volte para o site Slapie e aperte F5 (Recarregue a página)
              </Typography>
            </li>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Dica</AlertTitle>
            Se não encontrar o ícone na barra de tarefas, pressione a tecla{" "}
            <strong>⊞ Win</strong> e digite "Slapie" para encontrar o
            aplicativo.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleConfirmClose}
            color="primary"
            variant="contained"
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InternetConnectionModal;
